/**
 * assets/custom-scripts.js
 * ==============================
 * Custom JavaScript voor Hofman Animal Care.
 *
 * Bundelt klant-specifieke scripts. Geladen als ES-module via layout/theme.liquid.
 */

/* ============================================================
 * Product information - accordion onder de afbeelding plaatsen
 * ------------------------------------------------------------
 * De accordion is via de Theme Editor een block binnen .product-details
 * (rechterkolom). Het ontwerp vraagt de accordion onder de afbeelding in de
 * linkerkolom (.product-information__media). Omdat dit aparte grid-cellen zijn
 * en de accordion genest is, kan dit niet met CSS; we verplaatsen het element.
 *
 * - Desktop (>= 750px): accordion(s) onder de media-gallery plaatsen.
 * - Mobiel: accordion op de natuurlijke plek in de details laten staan.
 * - Werkt mee met de Theme Editor (her-sync bij shopify:section:load) en resize.
 * ============================================================ */

const DESKTOP_MEDIA_QUERY = window.matchMedia('(min-width: 750px)');

/**
 * Verplaatst de accordion(s) van een product-information component naar de
 * juiste kolom op basis van de viewport. Idempotent: meerdere keren aanroepen
 * is veilig.
 *
 * @param {HTMLElement} component - Het .product-information element.
 * @returns {void}
 */
const syncAccordionPlacement = (component) => {
  const media = component.querySelector('.product-information__media');
  const details = component.querySelector('.product-details');

  if (!media || !details) return;

  if (DESKTOP_MEDIA_QUERY.matches) {
    const accordions = details.querySelectorAll('.accordion');

    for (const accordion of accordions) {
      const home = document.createElement('div');
      home.setAttribute('data-custom-accordion-home', '');
      home.hidden = true;
      accordion.before(home);

      accordion.customAccordionHome = home;
      accordion.setAttribute('data-custom-relocated', '');
      media.append(accordion);
    }

    return;
  }

  const relocated = media.querySelectorAll('.accordion[data-custom-relocated]');

  for (const accordion of relocated) {
    const home = accordion.customAccordionHome;
    accordion.removeAttribute('data-custom-relocated');
    accordion.customAccordionHome = null;

    if (home && home.isConnected) {
      home.replaceWith(accordion);
      continue;
    }

    details.append(accordion);
  }
};

/**
 * Synchroniseert alle product-information componenten binnen een root.
 *
 * @param {ParentNode} [root=document] - Zoekwortel.
 * @returns {void}
 */
const syncAll = (root = document) => {
  const components = root.querySelectorAll('.product-information');

  for (const component of components) {
    syncAccordionPlacement(component);
  }
};

DESKTOP_MEDIA_QUERY.addEventListener('change', () => syncAll());

/* ============================================================
 * Product information - sticky add-to-cart bar verrijken
 * ------------------------------------------------------------
 * Het ontwerp toont in de sticky bar ook de review-rating (onder de titel) en
 * het voorraad-label (rechts naast de knop). Die zitten niet in de core
 * sticky-bar markup, maar staan wel elders op de productpagina. We klonen ze
 * daarom vanaf de pagina in de balk.
 *
 * De core morpht de balk bij een variantwissel (waardoor onze klonen
 * verdwijnen), dus her-injecteren we na het 'variant:update' event met een
 * verse kloon zodat o.a. de voorraadstatus klopt.
 * ============================================================ */

const STICKY_RATING_CLASS = 'custom-sticky__rating';
const STICKY_INVENTORY_CLASS = 'custom-sticky__inventory';

/**
 * Zoekt het bronelement op de pagina (buiten de sticky bar zelf).
 *
 * @param {ParentNode} root - Zoekwortel (de shopify-section).
 * @param {string} selector - CSS-selector van de bron.
 * @returns {Element | null}
 */
const findSourceOutsideSticky = (root, selector) =>
  Array.from(root.querySelectorAll(selector)).find((el) => !el.closest('sticky-add-to-cart')) ?? null;

/**
 * Kloont de rating en het voorraad-label vanaf de pagina in de sticky bar.
 * Idempotent: alleen toevoegen wanneer nog niet aanwezig.
 *
 * @param {HTMLElement} sticky - Het <sticky-add-to-cart> element.
 * @returns {void}
 */
const enhanceStickyBar = (sticky) => {
  const section = sticky.closest('.shopify-section');
  if (!section) return;

  const bar = sticky.querySelector('[ref="stickyBar"]');
  if (!bar) return;

  const info = bar.querySelector('.sticky-add-to-cart__info');
  const button = bar.querySelector('.sticky-add-to-cart__button');

  // Rating onder de titel
  if (info && !info.querySelector(`.${STICKY_RATING_CLASS}`)) {
    const ratingSource = findSourceOutsideSticky(section, '.rating-wrapper');

    if (ratingSource) {
      const ratingClone = ratingSource.cloneNode(true);
      ratingClone.classList.add(STICKY_RATING_CLASS);
      ratingClone.removeAttribute('style');
      // Dubbele SVG-defs verwijderen; de <use>-verwijzingen blijven werken via
      // de originele defs die al op de pagina staan.
      for (const defs of ratingClone.querySelectorAll('svg.visually-hidden')) {
        defs.remove();
      }
      info.append(ratingClone);
    }
  }

  // Voorraad-label vlak voor de knop (rechterkant)
  if (button && !bar.querySelector(`.${STICKY_INVENTORY_CLASS}`)) {
    const inventorySource = findSourceOutsideSticky(section, '.product-inventory__status');

    if (inventorySource) {
      const inventoryClone = inventorySource.cloneNode(true);
      inventoryClone.classList.add(STICKY_INVENTORY_CLASS);
      button.before(inventoryClone);
    }
  }
};

const MOBILE_MEDIA_QUERY = window.matchMedia('(max-width: 749px)');

/**
 * Op mobiel de sticky bar altijd tonen, behalve wanneer de footer in beeld komt.
 * Op desktop laat deze functie de core-logica ongemoeid.
 *
 * @param {HTMLElement} sticky - Het <sticky-add-to-cart> element.
 * @returns {void}
 */
const setupMobileStickyFooterHide = (sticky) => {
  if (sticky.dataset.customStickyMobileBound) return;

  const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
  if (!footer) return;

  sticky.dataset.customStickyMobileBound = 'true';

  let footerInView = false;

  const apply = () => {
    if (!MOBILE_MEDIA_QUERY.matches) {
      delete sticky.dataset.customMobile;
      return;
    }
    sticky.dataset.customMobile = footerInView ? 'hide' : 'show';
  };

  const footerObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0];
      if (!entry) return;
      footerInView = entry.isIntersecting;
      apply();
    },
    { rootMargin: '200px 0px 0px 0px' }
  );
  footerObserver.observe(footer);

  MOBILE_MEDIA_QUERY.addEventListener('change', apply);
  apply();
};

/**
 * Verrijkt alle sticky bars binnen een root en bindt de her-injectie aan
 * variantwijzigingen (eenmalig per sectie).
 *
 * @param {ParentNode} [root=document] - Zoekwortel.
 * @returns {void}
 */
const enhanceAllStickyBars = (root = document) => {
  for (const sticky of root.querySelectorAll('sticky-add-to-cart')) {
    enhanceStickyBar(sticky);
    setupMobileStickyFooterHide(sticky);

    const section = sticky.closest('.shopify-section');
    if (section instanceof HTMLElement && !section.dataset.customStickyBound) {
      section.dataset.customStickyBound = 'true';
      // Bij een variantwissel altijd verse klonen plaatsen, ongeacht of de
      // core-morph de oude klonen verwijderde. We zoeken het huidige sticky-
      // element op runtime op (kan vervangen zijn) en verwijderen bestaande
      // klonen voordat we opnieuw injecteren.
      section.addEventListener('variant:update', () => {
        setTimeout(() => {
          const currentSticky = section.querySelector('sticky-add-to-cart');
          if (!(currentSticky instanceof HTMLElement)) return;

          const bar = currentSticky.querySelector('[ref="stickyBar"]');
          if (bar) {
            for (const stale of bar.querySelectorAll(`.${STICKY_RATING_CLASS}, .${STICKY_INVENTORY_CLASS}`)) {
              stale.remove();
            }
          }

          enhanceStickyBar(currentSticky);
        }, 60);
      });
    }
  }
};

/* ============================================================
 * Custom 'Lees meer' tekstblok
 * ------------------------------------------------------------
 * Hoort bij blocks/custom-read-more-text.liquid. De inhoud wordt ingeklapt tot
 * een instelbaar aantal regels (via --custom-read-more-lines op het block) met
 * een 'Lees meer'/'Lees minder' toggle. We targeten een eigen, stabiele class
 * i.p.v. een vluchtig block-id.
 *
 * Progressive enhancement: zonder JS toont de tekst gewoon volledig (de
 * ingeklapte staat en de knop worden hier toegevoegd).
 * ============================================================ */

const READMORE_BLOCK = '.custom-read-more';
const COLLAPSED_CLASS = 'custom-read-more__content--collapsed';

let readMoreIdCounter = 0;

/**
 * Klapt de inhoud van elk custom 'Lees meer'-blok in en plaatst een toggle-knop
 * wanneer de tekst langer is dan het ingestelde aantal regels. Idempotent.
 *
 * @param {ParentNode} [root=document] - Zoekwortel.
 * @returns {void}
 */
const setupReadMore = (root = document) => {
  for (const block of root.querySelectorAll(READMORE_BLOCK)) {
    if (!(block instanceof HTMLElement) || block.dataset.customReadmoreReady) continue;

    const content = block.querySelector('.custom-read-more__content');
    if (!(content instanceof HTMLElement)) continue;

    block.dataset.customReadmoreReady = 'true';
    content.classList.add(COLLAPSED_CLASS);

    // Alleen een knop tonen als de tekst daadwerkelijk wordt afgekapt.
    if (content.scrollHeight - content.clientHeight <= 1) {
      content.classList.remove(COLLAPSED_CLASS);
      continue;
    }

    if (!content.id) {
      readMoreIdCounter += 1;
      content.id = `custom-read-more-content-${readMoreIdCounter}`;
    }

    const labelMore = block.dataset.labelMore || 'Lees meer';
    const labelLess = block.dataset.labelLess || 'Lees minder';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'custom-read-more__toggle';
    toggle.textContent = labelMore;
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', content.id);

    toggle.addEventListener('click', () => {
      const collapsed = content.classList.toggle(COLLAPSED_CLASS);
      toggle.setAttribute('aria-expanded', String(!collapsed));
      toggle.textContent = collapsed ? labelMore : labelLess;
    });

    block.append(toggle);
  }
};

// Theme Editor: secties worden bij block-wijzigingen opnieuw gerenderd.
document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  syncAll(event.target);
  enhanceAllStickyBars(event.target);
  setupReadMore(event.target);
});

// Na een variantwissel morpht de sectie; de omschrijving + knop kunnen opnieuw
// gerenderd zijn, dus opnieuw opzetten.
document.addEventListener('variant:update', () => {
  setTimeout(() => setupReadMore(), 60);
});

syncAll();
enhanceAllStickyBars();

/* ============================================================
 * Microsoft Clarity - consent-gated + idle load
 * ------------------------------------------------------------
 * Voorheen geladen via de Clarity app-embed, die het script direct in de
 * main thread injecteerde (~200ms exec tijdens de pageload -> hoge TBT).
 * Nu zelf beheerd, zodat we het laadmoment controleren:
 *
 * - Consent: laadt pas als analytics-toestemming is gegeven via Shopify's
 *   Customer Privacy API (de standaard Shopify cookie-banner). Bij weigeren
 *   laadt Clarity helemaal niet.
 * - Timing: geladen op requestIdleCallback, dus buiten het kritieke
 *   render-venster -> geen concurrentie met LCP en lagere TBT.
 * - Doelgroep: voor alle bezoekers (ook ingelogd).
 * ============================================================ */

const CLARITY_PROJECT_ID = 'xmd9jizvnn';

/**
 * Injecteert de officiele Clarity-snippet. Idempotent.
 * @returns {void}
 */
const injectClarity = () => {
  if (window.__clarityLoaded || window.clarity) return;
  window.__clarityLoaded = true;

  (function (c, l, a, r, i, t, y) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    t = l.createElement(r);
    t.async = 1;
    t.src = 'https://www.clarity.ms/tag/' + i;
    y = l.getElementsByTagName(r)[0];
    y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', CLARITY_PROJECT_ID);
};

/**
 * Laadt Clarity zodra de main thread rust heeft, met een timeout-fallback
 * zodat het bij een drukke thread alsnog binnen redelijke tijd start.
 * @returns {void}
 */
const loadClarityWhenIdle = () => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(injectClarity, { timeout: 3000 });
  } else {
    setTimeout(injectClarity, 2000);
  }
};

/**
 * @returns {boolean} Of de bezoeker analytics-toestemming heeft gegeven.
 */
const analyticsAllowed = () => {
  const cp = window.Shopify && window.Shopify.customerPrivacy;
  try {
    return Boolean(cp && cp.analyticsProcessingAllowed && cp.analyticsProcessingAllowed());
  } catch (error) {
    return false;
  }
};

/**
 * Laadt Clarity als er al toestemming is; zo niet, wacht op de consent-keuze.
 * @returns {void}
 */
const initClarity = () => {
  if (analyticsAllowed()) {
    loadClarityWhenIdle();
    return;
  }
  document.addEventListener('visitorConsentCollected', () => {
    if (analyticsAllowed()) loadClarityWhenIdle();
  });
};

// Zorg dat de consent-API geladen is voordat we de toestemming uitlezen.
if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], initClarity);
} else {
  initClarity();
}
setupReadMore();

/* ============================================================
 * Google Tag Manager + GA4 - Consent Mode v2 + idle load
 * ------------------------------------------------------------
 * US-INT-05. Container GTM-T72V767R draait naast een tweede instantie van
 * hetzelfde container in de custom Web Pixel (checkout/thank-you, zie
 * scripts/web-pixel/pixel.js) - zie docs/ANALYTICS-GA4-GTM.md voor de
 * volledige architectuur en de reden waarom checkout niet vanuit het thema
 * te bereiken is.
 *
 * - dataLayer + Consent Mode v2 default ('denied') staan VOOR het laden van
 *   de container, zodat GTM/GA4 nooit zonder consent-status tags afvuurt.
 *   Bij "denied" blijft GA4 toch pageviews tellen via een cookieless
 *   modeling-ping (i.t.t. Clarity hierboven, dat volledig blokkeert).
 * - Timing: idle-load zoals Clarity, maar met een kortere timeout omdat
 *   pageview-telling (bounce/exit-gedrag) tijdgevoelig is; de container zelf
 *   is bovendien lichter dan Clarity's session-recording script.
 * - customer_type/company_* dataLayer-variabelen (B2B) staan al klaar vóór
 *   dit blok, gepusht vanuit snippets/custom-head.liquid (server-side via
 *   customer.current_company), zodat GTM ze als initiële staat meeneemt.
 * ============================================================ */

const GTM_CONTAINER_ID = 'GTM-T72V767R';

window.dataLayer = window.dataLayer || [];

/**
 * @returns {void}
 */
function gtag() {
  window.dataLayer.push(arguments);
}

// Consent Mode v2: standaard alles geweigerd totdat Shopify's consent-API
// een keuze doorgeeft. `wait_for_update` geeft de banner ruimte om op tijd
// te reageren zonder dat er al tags met verkeerde consent-status vuren.
gtag('consent', 'default', {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
  wait_for_update: 500,
});

/**
 * Injecteert de officiele GTM-container. Idempotent.
 * @returns {void}
 */
const injectGtm = () => {
  if (window.__gtmLoaded) return;
  window.__gtmLoaded = true;

  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
  document.head.appendChild(script);
};

/**
 * @returns {void}
 */
const loadGtmWhenIdle = () => {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(injectGtm, { timeout: 1500 });
  } else {
    setTimeout(injectGtm, 1000);
  }
};

/**
 * Vertaalt Shopify's Customer Privacy-toestemmingen naar Google Consent
 * Mode v2-velden.
 * @returns {void}
 */
const updateConsentFromShopify = () => {
  const cp = window.Shopify && window.Shopify.customerPrivacy;
  if (!cp) return;

  const granted = (fn) => {
    try {
      return Boolean(cp[fn] && cp[fn]());
    } catch (error) {
      return false;
    }
  };

  const analytics = granted('analyticsProcessingAllowed') ? 'granted' : 'denied';
  const marketing = granted('marketingAllowed') ? 'granted' : 'denied';
  const preferences = granted('preferencesProcessingAllowed') ? 'granted' : 'denied';

  gtag('consent', 'update', {
    analytics_storage: analytics,
    ad_storage: marketing,
    ad_user_data: marketing,
    ad_personalization: marketing,
    personalization_storage: preferences,
  });
};

const initGtm = () => {
  updateConsentFromShopify();
  loadGtmWhenIdle();
};

if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
  window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], initGtm);
} else {
  loadGtmWhenIdle();
}

// Consent kan na de eerste load nog wijzigen (banner, instellingen-pagina).
document.addEventListener('visitorConsentCollected', updateConsentFromShopify);

/* ============================================================
 * Shopify Forms - knop-gewicht binnen de shadow DOM
 * ------------------------------------------------------------
 * De Forms-app rendert <shopify-forms-embed> met een open shadow
 * root. CSS uit assets/custom-styles.css komt daar niet binnen;
 * alleen custom properties erven mee (zie het Forms-blok daar,
 * dat regelt kleur, radius en padding van de knop).
 *
 * Voor de font-weight bestaat geen eigen app-token: het gewicht
 * komt uit --forms-body-font en die geldt voor alle tekst in het
 * formulier. Om alleen de verzendknop het Accent-gewicht te geven
 * (zoals .button in base.css) hangen we een <style> in de shadow
 * root zelf. --button-font-weight-primary erft daar naar binnen,
 * dus de waarde blijft uit de theme-instellingen komen.
 *
 * Selector op [type="submit"] i.p.v. de class van de app: die is
 * gehasht (_formSubmitButton_jnbzb_106) en verandert per release.
 * ============================================================ */

const FORMS_SHADOW_CSS = `
  button[type='submit'] {
    /* !important omdat adoptedStyleSheets van de app later in de cascade
       komen dan een <style> in de shadow root, en haar eigen regel
       (button._formSubmitButton_<hash>) even specifiek is als deze. */
    font-weight: var(--button-font-weight-primary, 700) !important;
  }
`;

/**
 * Hangt de stylesheet in de shadow root van een forms-embed.
 * Idempotent: een tweede aanroep voor hetzelfde element doet niets.
 *
 * @param {Element} host - Het <shopify-forms-embed> element.
 * @returns {boolean} true zodra de style daadwerkelijk hangt.
 */
const styleFormsEmbed = (host) => {
  const root = host.shadowRoot;

  // De shadow root wordt pas na het upgraden van het element gezet.
  if (!root) return false;
  if (root.querySelector('style[data-custom-forms]')) return true;

  const style = document.createElement('style');
  style.dataset.customForms = '';
  style.textContent = FORMS_SHADOW_CSS;

  root.append(style);

  return true;
};

/**
 * Zoekt alle forms-embeds op de pagina en styleert wat al klaar is.
 * @returns {boolean} true wanneer elke embed op de pagina gestyled is.
 */
const syncFormsEmbeds = () => {
  const hosts = [...document.querySelectorAll('shopify-forms-embed')];

  return hosts.length > 0 && hosts.every(styleFormsEmbed);
};

/**
 * De app laadt asynchroon en de shadow root verschijnt pas na het upgraden
 * van het element. Dat moment produceert geen mutatie in de light DOM, dus
 * een MutationObserver alleen is niet genoeg: we pollen kort mee.
 *
 * @returns {void}
 */
const pollFormsEmbeds = () => {
  const deadline = Date.now() + 15000;

  const tick = () => {
    if (syncFormsEmbeds() || Date.now() > deadline) return;
    setTimeout(tick, 250);
  };

  tick();
};

pollFormsEmbeds();

// Nieuwe embeds (pop-up die pas bij een klik rendert, of een block dat de
// Theme Editor opnieuw plaatst) alsnog oppikken.
let formsSyncQueued = false;

const queueFormsSync = () => {
  if (formsSyncQueued) return;

  formsSyncQueued = true;
  requestAnimationFrame(() => {
    formsSyncQueued = false;
    syncFormsEmbeds();
  });
};

new MutationObserver(queueFormsSync).observe(document.body, { childList: true, subtree: true });

document.addEventListener('shopify:section:load', pollFormsEmbeds);


/* ============================================================
 * Ankerlink naar een accordion-rij openen
 * ------------------------------------------------------------
 * Een link als #accordion-accordion_row_cwxi7f (knop "Meer specificaties")
 * scrolt standaard naar een dichte <details>, of doet niets zichtbaars.
 * Hier klappen we de rij open en scrollen we ernaartoe, ook bij het laden
 * van een URL met die hash. _accordion-row zet het id op <details>.
 * ============================================================ */

/**
 * Opent de <details> waar de hash naar wijst en scrolt ernaartoe.
 *
 * @param {string} hash - Bijvoorbeeld '#accordion-accordion_row_cwxi7f'.
 * @param {boolean} [focusSummary] - Zet de focus op de kop van de rij, zodat
 *   toetsenbordnavigatie daar verdergaat (zoals bij een gewone ankerlink).
 * @returns {boolean} Of er een accordion-rij gevonden is.
 */
const openAccordionFromHash = (hash, focusSummary = false) => {
  if (!hash || hash.length < 2) return false;

  const target = document.getElementById(decodeURIComponent(hash.slice(1)));
  const details = target?.closest('details');

  if (!details) return false;

  details.open = true;
  details.scrollIntoView({ behavior: 'smooth', block: 'start' });

  if (focusSummary) {
    const summary = details.querySelector('summary');
    if (summary instanceof HTMLElement) summary.focus({ preventScroll: true });
  }

  return true;
};

document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;

  if (!link || !openAccordionFromHash(link.getAttribute('href'), true)) return;

  event.preventDefault();
  // pushState (geen replaceState): Terug gaat dan naar de vorige hash. Dit
  // vuurt geen hashchange af, dus er wordt niet dubbel gescrold.
  history.pushState(null, '', link.getAttribute('href'));
});

// Pas na load: de accordion wordt op desktop eerst verplaatst, en
// accordion-custom zet bij (her)plaatsen de standaard open-stand terug.
window.addEventListener('load', () => {
  setTimeout(() => openAccordionFromHash(window.location.hash));
});

// Terug/Vooruit naar een andere hash na het laden.
window.addEventListener('hashchange', () => {
  openAccordionFromHash(window.location.hash);
});
